import { Box, Card, Typography, Grid } from "@mui/joy";
import { useInspectionAttributes } from "../../../../hooks/query";
import { useProject } from "../../../../hooks";

import { TearOff } from "../TearOff";
import { ColorSelection } from "../ColorSelection";
import { RoofPitch } from "../RoofPitch";
import { VentilationType } from "../VentilationType";
import { VentilationPainted } from "../VentilationPainted";
import { ValleyType } from "../ValleyType";
import { ChimneyType } from "../ChimneyType";
import { ChimneyColor } from "../ChimneyColor";
import { EaveDripEdge } from "../EaveDripEdge";
import { RakeDripEdge } from "../RakeDripEdge";
import { FurnaceVentJackSize } from "../FurnaceVentJackSize";
import { FurnaceVentCapCollar } from "../FurnaceVentCapCollar";
import { ModeOfPayment } from "../ModeOfPayment";
import { ChimneySize } from "../ChimneySize";
import { SkylightType } from "../SkylightType";
import { SkylightSize } from "../SkylightSize";
import { EvaporativeCooler } from "../EvaporativeCooler";
import { GutterGuardType } from "../GutterGuardType";

export function AdditionalAttributes(props: { attributes: any; setAttributes: any }) {
    const { attributes, setAttributes } = props;
    const { project } = useProject();
    const { useUpdateSingleAttribute } = useInspectionAttributes();
    const updateAttributeSvc = useUpdateSingleAttribute();

    const handleUpdate = async (value: string, attributeName: string) => {
        try {
            if (!project?.id) {
                console.error(`Cannot update ${attributeName}: Project ID not found.`);
                return;
            }
            const attribute = attributes?.find((attr: any) => attr.AttributeName === attributeName);
            const attributeId = attribute?.Id;

            const updateParams: any = { Value: value };
            if (attributeId) {
                updateParams.Id = attributeId;
            } else {
                updateParams.ProjectId = project.id;
                updateParams.AttributeName = attributeName;
            }

            const response = await updateAttributeSvc.mutateAsync(updateParams);

            if (attributeId) {
                setAttributes((prev: any[]) =>
                    prev.map((attr) =>
                        attr.AttributeName === attributeName ? { ...attr, AttributeValue: value } : attr,
                    ),
                );
            } else {
                const newAttribute = {
                    Id: response?.Id || attribute?.Id,
                    AttributeName: attributeName,
                    AttributeValue: value,
                };
                setAttributes((prev: any[]) => {
                    const exists = prev.some((attr) => attr.AttributeName === attributeName);
                    if (!exists) return [...prev, newAttribute];
                    return prev.map((attr) =>
                        attr.AttributeName === attributeName ? { ...attr, ...newAttribute } : attr,
                    );
                });
            }
        } catch (error) {
            console.error(`Error updating ${attributeName}:`, error);
        }
    };

    const getAttrValue = (name: string) => attributes?.find((attr: any) => attr.AttributeName === name)?.AttributeValue;

    return (
        <Box sx={{ mt: 4, display: "flex", flexDirection: "column", gap: 4 }}>
            <Box>
                <Typography level="title-sm" sx={{ color: "text.tertiary", mb: 1.5, textTransform: "uppercase", letterSpacing: "0.5px", fontSize: "11px", fontWeight: "xl" }}>
                    Roof Details
                </Typography>
                <Card variant="outlined" sx={{ p: 2, bgcolor: "background.surface", boxShadow: "none" }}>
                    <Grid container spacing={2}>
                        <Grid xs={12} sm={6} md={4}>
                            <ColorSelection
                                value={getAttrValue("Roof Color")}
                                onChange={(v) => handleUpdate(v, "Roof Color")}
                            />
                        </Grid>
                        <Grid xs={12} sm={6} md={4}>
                            <RoofPitch value={getAttrValue("Roof Pitch")} onChange={(v) => handleUpdate(v, "Roof Pitch")} />
                        </Grid>
                        <Grid xs={12} sm={6} md={4}>
                            <TearOff
                                value={getAttrValue("Tear Off Layer")}
                                onChange={(v) => handleUpdate(v, "Tear Off Layer")}
                            />
                        </Grid>
                        <Grid xs={12} sm={6} md={4}>
                            <VentilationType
                                value={getAttrValue("Ventilation Type")}
                                onChange={(v) => handleUpdate(v, "Ventilation Type")}
                            />
                        </Grid>
                        <Grid xs={12} sm={6} md={4}>
                            <VentilationPainted
                                value={getAttrValue("Ventilation Painted")}
                                onChange={(v) => handleUpdate(v, "Ventilation Painted")}
                            />
                        </Grid>
                    </Grid>
                </Card>
            </Box>

            <Box>
                <Typography level="title-sm" sx={{ color: "text.tertiary", mb: 1.5, textTransform: "uppercase", letterSpacing: "0.5px", fontSize: "11px", fontWeight: "xl" }}>
                    Furnace Vent
                </Typography>
                <Card variant="outlined" sx={{ p: 2, bgcolor: "background.surface", boxShadow: "none" }}>
                    <Grid container spacing={2}>
                        <Grid xs={12} sm={6}>
                            <FurnaceVentJackSize
                                value={getAttrValue("Furnace Vent Jack Size")}
                                onChange={(v) => handleUpdate(v, "Furnace Vent Jack Size")}
                            />
                        </Grid>
                        <Grid xs={12} sm={6}>
                            <FurnaceVentCapCollar
                                value={getAttrValue("Furnace Vent Cap and Collar")}
                                onChange={(v) => handleUpdate(v, "Furnace Vent Cap and Collar")}
                            />
                        </Grid>
                    </Grid>
                </Card>
            </Box>

            <Box>
                <Typography level="title-sm" sx={{ color: "text.tertiary", mb: 1.5, textTransform: "uppercase", letterSpacing: "0.5px", fontSize: "11px", fontWeight: "xl" }}>
                    Drip Edge
                </Typography>
                <Card variant="outlined" sx={{ p: 2, bgcolor: "background.surface", boxShadow: "none" }}>
                    <Grid container spacing={2}>
                        <Grid xs={12} sm={6}>
                            <EaveDripEdge
                                value={getAttrValue("Eave Drip Edge")}
                                onChange={(v) => handleUpdate(v, "Eave Drip Edge")}
                            />
                        </Grid>
                        <Grid xs={12} sm={6}>
                            <RakeDripEdge
                                value={getAttrValue("Rake Drip Edge")}
                                onChange={(v) => handleUpdate(v, "Rake Drip Edge")}
                            />
                        </Grid>
                    </Grid>
                </Card>
            </Box>

            <Box>
                <Typography level="title-sm" sx={{ color: "text.tertiary", mb: 1.5, textTransform: "uppercase", letterSpacing: "0.5px", fontSize: "11px", fontWeight: "xl" }}>
                    Components & Equipment
                </Typography>
                <Card variant="outlined" sx={{ p: 2, bgcolor: "background.surface", boxShadow: "none" }}>
                    <Grid container spacing={2}>
                        <Grid xs={12} sm={6} md={4}>
                            <ChimneySize
                                value={getAttrValue("Chimney Size")}
                                onChange={(v) => handleUpdate(v, "Chimney Size")}
                            />
                        </Grid>
                        <Grid xs={12} sm={6} md={4}>
                            <SkylightType
                                value={getAttrValue("Skylight Type")}
                                onChange={(v) => handleUpdate(v, "Skylight Type")}
                            />
                        </Grid>
                        <Grid xs={12} sm={6} md={4}>
                            <SkylightSize
                                value={getAttrValue("Skylight Size")}
                                onChange={(v) => handleUpdate(v, "Skylight Size")}
                            />
                        </Grid>
                        <Grid xs={12} sm={6} md={4}>
                            <EvaporativeCooler
                                value={getAttrValue("Has Evaporative Cooler")}
                                onChange={(v) => handleUpdate(v, "Has Evaporative Cooler")}
                            />
                        </Grid>
                        <Grid xs={12} sm={6} md={4}>
                            <GutterGuardType
                                value={getAttrValue("Gutter Guard Type")}
                                onChange={(v) => handleUpdate(v, "Gutter Guard Type")}
                            />
                        </Grid>
                    </Grid>
                </Card>
            </Box>

            <Box>
                <Typography level="title-sm" sx={{ color: "text.tertiary", mb: 1.5, textTransform: "uppercase", letterSpacing: "0.5px", fontSize: "11px", fontWeight: "xl" }}>
                    Other
                </Typography>
                <Card variant="outlined" sx={{ p: 2, bgcolor: "background.surface", boxShadow: "none" }}>
                    <Grid container spacing={2}>
                        <Grid xs={12} sm={6}>
                            <ValleyType
                                value={getAttrValue("Valley Type")}
                                onChange={(v) => handleUpdate(v, "Valley Type")}
                            />
                        </Grid>
                        <Grid xs={12} sm={6}>
                            <ChimneyType
                                value={getAttrValue("Chimney Type")}
                                onChange={(v) => handleUpdate(v, "Chimney Type")}
                            />
                        </Grid>
                        <Grid xs={12} sm={6}>
                            <ChimneyColor
                                value={getAttrValue("Chimney Color")}
                                onChange={(v) => handleUpdate(v, "Chimney Color")}
                            />
                        </Grid>
                        <Grid xs={12} sm={6}>
                            <ModeOfPayment
                                value={getAttrValue("Mode of Payment")}
                                onChange={(v) => handleUpdate(v, "Mode of Payment")}
                            />
                        </Grid>
                    </Grid>
                </Card>
            </Box>
        </Box>
    );
}
